import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Users, Gift, FileText } from "tabler-icons-react";
import { useTranslation } from "react-i18next";

const primary = "#43bd98";
const accent = "#4A90E2";

const fadeUp = {
	hidden: { opacity: 0, y: 40, filter: "blur(8px)" },
	show: {
		opacity: 1,
		y: 0,
		filter: "blur(0px)",
		transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
	},
};

const staggerContainer = {
	hidden: {},
	show: {
		transition: { staggerChildren: 0.15, delayChildren: 0.1 },
	},
};

const cardVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: (i) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
	}),
};

export default function TrainerFeatures() {
	const { t } = useTranslation();
	const [start, setStart] = useState(false);

	useEffect(() => {
		const tmo = setTimeout(() => setStart(true));
		return () => clearTimeout(tmo);
	}, []);

	const features = t("trainer.features.list", { returnObjects: true });

	return (
		<section
			style={{
				padding: "6rem 2rem",
				background: "#ffffff",
				fontFamily: "'Poppins', sans-serif",
				scrollMarginTop: "100px",
			}}
			id="funcionalidades"
		>
			{/* Encabezado */}
			<motion.div
				key={start}
				initial={{ opacity: 0, y: -30 }}
				animate={start ? { opacity: 1, y: 0 } : {}}
				transition={{ duration: 0.8, ease: "easeOut" }}
				style={{ textAlign: "center", marginBottom: "3.5rem" }}
			>
				<p
					style={{
						color: accent,
						fontSize: "0.9rem",
						textTransform: "uppercase",
						letterSpacing: "1px",
						fontWeight: 600,
						marginBottom: "0.5rem",
					}}
				>
					{t("trainer.features.badge")}
				</p>

				<h2
					style={{
						fontSize: "clamp(2rem, 5vw, 2.8rem)",
						fontWeight: 800,
						color: "#1a1a1a",
						lineHeight: 1.3,
						letterSpacing: "-0.5px",
						margin: 0,
					}}
				>
					{t("trainer.features.title")}
				</h2>
			</motion.div>

			{/* App y Web */}
			<motion.div
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true, amount: 0.2 }}
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
					gap: "2.5rem",
					maxWidth: "1000px",
					margin: "0 auto",
				}}
			>
				{["app", "web"].map((type) => (
					<motion.div
						key={type}
						whileHover={{
							scale: 1.02,
							boxShadow: "0 12px 24px rgba(0, 0, 0, 0.08)",
						}}
						style={{
							borderRadius: "16px",
							backgroundColor: "#f9fefc",
							padding: "2.5rem",
							border: "1px solid #e1f3ec",
							boxShadow: "0 8px 16px rgba(0, 0, 0, 0.04)",
							cursor: "default",
						}}
					>
						<h3
							style={{
								fontSize: "1.5rem",
								marginBottom: "1rem",
								color: "#1a1a1a",
								fontWeight: 700,
							}}
						>
							{t(`trainer.features.${type}.title`)}
						</h3>
						<p
							style={{
								color: "#444",
								lineHeight: "1.6",
								fontSize: "1rem",
								marginBottom: "1rem",
							}}
						>
							{t(`trainer.features.${type}.desc`)}
						</p>
						<ul
							style={{
								color: "#444",
								lineHeight: "1.8",
								fontSize: "1rem",
								paddingLeft: "1.2rem",
								listStyle: "disc",
							}}
						>
							{t(`trainer.features.${type}.items`, {
								returnObjects: true,
							}).map((i) => (
								<li key={i}>{i}</li>
							))}
						</ul>
					</motion.div>
				))}
			</motion.div>

			{/* Segunda parte - funcionalidades */}
			<motion.div
				variants={fadeUp}
				initial="hidden"
				whileInView="show"
				viewport={{ once: true, amount: 0.2 }}
				style={{
					marginTop: "12rem",
					backgroundColor: "#fff",
					fontFamily: "'Poppins', sans-serif",
				}}
			>
				<p
					className="uppercase tracking-wider font-semibold mb-4 text-center"
					style={{ color: accent }}
				>
					{t("trainer.features.section2.badge")}
				</p>

				<h2
					className="mb-20 tracking-tight text-center"
					style={{
						fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
						fontWeight: 800,
						color: "#222",
					}}
					dangerouslySetInnerHTML={{
						__html: t("trainer.features.section2.title"),
					}}
				/>

				<motion.div
					variants={staggerContainer}
					initial="hidden"
					whileInView="show"
					viewport={{ once: true, amount: 0.15 }}
					className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 max-w-6xl mx-auto px-6"
				>
					{features.map((f, i) => (
						<motion.div
							key={f.title}
							className="bg-white rounded-xl p-6 border border-gray-200 flex flex-col items-center text-center cursor-pointer transition-colors"
							variants={cardVariants}
							initial="hidden"
							whileInView="visible"
							custom={i}
							viewport={{ once: true }}
							whileHover={{
								scale: 1.05,
								y: -6,
								boxShadow: "0 12px 24px rgba(0,0,0,0.04)",
								borderColor: primary,
								transition: { duration: 0.2 },
							}}
							whileTap={{ scale: 0.97 }}
						>
							<div
								className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
								style={{ backgroundColor: `${primary}15` }}
							>
								{
									[
										<Calendar size={42} strokeWidth={1.3} color={accent} />,
										<Users size={42} strokeWidth={1.3} color={accent} />,
										<Gift size={42} strokeWidth={1.3} color={accent} />,
										<FileText size={42} strokeWidth={1.3} color={accent} />,
									][i]
								}
							</div>
							<h3 className="mt-2 font-semibold text-gray-900">{f.title}</h3>
							<p className="text-sm text-gray-600 mt-2">{f.text}</p>
						</motion.div>
					))}
				</motion.div>
			</motion.div>
		</section>
	);
}
